import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { EndpointBase } from './endpoint-base.service';
import { AuthService } from './auth.service';
import { ConfigurationService } from './configuration.service';

export interface PaymentRequestDto {
  id: string;
  accountId: string;
  accountName: string;
  rcContact: string;
  amount?: number;
  currency: string;
  status: string;
  hubId: string;
  hubName: string;
  createdDate: string;
  updatedDate: string;
  lastModifiedBy: string;
  salesforceRecordId?: string;
  isSynced: boolean;
  isEditable: boolean;
}
export interface PaymentRequestUpdateDto {
  id: string;

  debitAmount?: number;
  outOfSecurity?: number;
  paymentintoSecurity?: number;
  steringPayment?: number;
  ckeOperationPayment?: number;
  ckeLogoWearPayment?: number;
  totalAmount?: number;
  totalPayment?: number;
  comments?: string;
}

export interface ApiResponse<T> {
  isSuccess: boolean;
  data: T;
  message: string;
  errorCode?: string;
  errors?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class PaymentRequestService extends EndpointBase {

  private apiUrl: string;

  constructor(
    configurations: ConfigurationService,
    http: HttpClient,
    authService: AuthService
  ) {
    super(http, authService);
    this.apiUrl = configurations.baseUrl + '/api/paymentrequest';
  }

  // ✅ GET ALL
  getAllPaymentRequests(): Observable<ApiResponse<PaymentRequestDto[]>> {
    return this.http.get<ApiResponse<PaymentRequestDto[]>>(
      `${this.apiUrl}/all`,
      this.requestHeaders
    );
  }

  // ✅ GET BY HUB (optional if still needed)
  getPaymentRequestsByHub(hubId: string): Observable<ApiResponse<PaymentRequestDto[]>> {
    return this.http.get<ApiResponse<PaymentRequestDto[]>>(
      `${this.apiUrl}/byhub/${hubId}`,
      this.requestHeaders
    );
  }

  // ✅ GET SINGLE
  getPaymentRequest(id: string): Observable<ApiResponse<PaymentRequestDto>> {
    return this.http.get<ApiResponse<PaymentRequestDto>>(
      `${this.apiUrl}/${id}`,
      this.requestHeaders
    );
  }

  // ✅ CREATE
  createPaymentRequest(request: PaymentRequestDto): Observable<ApiResponse<PaymentRequestDto>> {
    return this.http.post<ApiResponse<PaymentRequestDto>>(
      `${this.apiUrl}/create`,
      request,
      this.requestHeaders
    );
  }

  // ✅ UPDATE (USED IN AUTO-SAVE)
  updatePaymentRequest(id: string, request: PaymentRequestUpdateDto): Observable<ApiResponse<PaymentRequestUpdateDto>> {
    return this.http.put<ApiResponse<PaymentRequestUpdateDto>>(
      `${this.apiUrl}/${id}`,
      request,
      this.requestHeaders
    );
  }

  // ✅ CHANGED RECORDS
  getChangedRecords(since: string): Observable<ApiResponse<PaymentRequestDto[]>> {
    return this.http.get<ApiResponse<PaymentRequestDto[]>>(
      `${this.apiUrl}/changed?since=${since}`,
      this.requestHeaders
    );
  }

  // ✅ CHECK EDIT WINDOW (optional backend validation)
  checkEditableTime(): Observable<{ isEditable: boolean; message: string }> {
    return this.http.get<{ isEditable: boolean; message: string }>(
      `${this.apiUrl}/status/editable-time`,
      this.requestHeaders
    );
  }

  // ✅ SYNC ALL
  syncToSalesforce(): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this.apiUrl}/sync-salesforce`,
      {},
      this.requestHeaders
    );
  }

  // ✅ GET UNSYNCED
  getUnsyncedRecords(): Observable<ApiResponse<PaymentRequestDto[]>> {
    return this.http.get<ApiResponse<PaymentRequestDto[]>>(
      `${this.apiUrl}/unsynced`,
      this.requestHeaders
    );
  }

  // ✅ SYNC SINGLE
  syncSingleRecord(id: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this.apiUrl}/${id}/sync`,
      {},
      this.requestHeaders
    );
  }
}
